import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, X, Phone, Mail, ArrowRight } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { divisions } from '@/data/divisions'

interface MobileMenuProps {
  open: boolean
  onClose: () => void
}

export function MobileMenu({ open, onClose }: MobileMenuProps) {
  const { t } = useTranslation()
  const [showDivisions, setShowDivisions] = useState(false)

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onClose}
          />

          {/* Menu Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-y-0 right-0 w-[85vw] max-w-[360px] z-50 bg-white shadow-2xl lg:hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <span className="font-semibold text-gray-900">Menu</span>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label={t('aria.closeMenu')}
              >
                <X className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto py-2" aria-label="Mobile">
              {/* Home */}
              <LocalizedLink
                to="home"
                onClick={onClose}
                className="flex items-center px-5 py-3.5 text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
              >
                {t('nav.home')}
              </LocalizedLink>

              {/* About */}
              <LocalizedLink
                to="about"
                onClick={onClose}
                className="flex items-center px-5 py-3.5 text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
              >
                {t('nav.about')}
              </LocalizedLink>

              {/* Divisions with preview - solo visualizzazione, non link singoli */}
              <div className="border-t border-gray-100 mt-2 pt-2">
                <button
                  onClick={() => setShowDivisions(!showDivisions)}
                  className="flex items-center justify-between w-full px-5 py-3.5 text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {t('nav.divisions')}
                  <motion.div
                    animate={{ rotate: showDivisions ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {showDivisions && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden bg-gray-50"
                    >
                      {/* Preview delle divisioni - solo visualizzazione */}
                      <div className="px-5 py-3">
                        <div className="flex flex-wrap gap-2">
                          {divisions.slice(0, 6).map((division) => (
                            <div
                              key={division.id}
                              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-xs text-gray-600"
                            >
                              <span
                                className="h-2 w-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: division.colorHex }}
                              />
                              <span className="truncate">{t(division.nameKey)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Link unico a tutte le divisioni */}
                      <LocalizedLink
                        to="divisions"
                        onClick={onClose}
                        className="flex items-center justify-between px-5 py-3 text-sm font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors border-t border-gray-100"
                      >
                        {t('nav.viewAllDivisions')}
                        <ArrowRight className="h-4 w-4" />
                      </LocalizedLink>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other links */}
              <div className="border-t border-gray-100 mt-2 pt-2">
                <LocalizedLink
                  to="certifications"
                  onClick={onClose}
                  className="flex items-center px-5 py-3.5 text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {t('nav.certifications')}
                </LocalizedLink>
                <LocalizedLink
                  to="careers"
                  onClick={onClose}
                  className="flex items-center px-5 py-3.5 text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {t('nav.careers')}
                </LocalizedLink>
                <LocalizedLink
                  to="contacts"
                  onClick={onClose}
                  className="flex items-center px-5 py-3.5 text-base font-medium text-gray-900 hover:bg-gray-50 transition-colors"
                >
                  {t('nav.contacts')}
                </LocalizedLink>
              </div>
            </nav>

            {/* Footer */}
            <div className="border-t border-gray-100 p-4 space-y-4">
              {/* Quick Contact */}
              <div className="flex gap-2">
                <a
                  href="tel:051.6815027"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium text-sm hover:bg-gray-200 transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Chiama
                </a>
                <LocalizedLink
                  to="contacts"
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-medium text-sm hover:bg-primary-700 transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  Contatti
                </LocalizedLink>
              </div>

              {/* Language Selector */}
              <div className="pt-2 border-t border-gray-100">
                <LanguageSelector variant="mobile" />
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
